import { Button, Form, Input, InputNumber, Select, Space } from 'antd';
import type { DoohOrientation, DoohScreen, UpdateDoohScreenBody } from '@goelectric/shared-types';
import { ORIENTATION_LABELS } from '../utils/format';

export interface ScreenConfigFormProps {
  screen: DoohScreen;
  saving: boolean;
  onSave: (body: UpdateDoohScreenBody) => void;
}

interface ScreenConfigValues {
  name: string;
  locationId?: string;
  allyId?: string;
  resolutionWidth?: number | null;
  resolutionHeight?: number | null;
  orientation?: DoohOrientation | null;
  timezone?: string;
}

const ORIENTATION_OPTIONS = (['LANDSCAPE', 'PORTRAIT'] as const).map((value) => ({
  value,
  label: ORIENTATION_LABELS[value],
}));

/**
 * Formulario de configuración de una pantalla DOOH (TG-59): nombre, ubicación/aliado,
 * resolución, orientación y zona horaria. PATCH parcial: solo se envían los campos
 * del contrato (`UpdateDoohScreenBody`).
 */
export function ScreenConfigForm({ screen, saving, onSave }: ScreenConfigFormProps) {
  const [form] = Form.useForm<ScreenConfigValues>();

  const submit = (values: ScreenConfigValues) => {
    const body: UpdateDoohScreenBody = {
      name: (values.name ?? '').trim(),
      locationId: normalizeOptional(values.locationId),
      allyId: normalizeOptional(values.allyId),
      resolutionWidth: values.resolutionWidth ?? null,
      resolutionHeight: values.resolutionHeight ?? null,
      orientation: values.orientation ?? null,
      timezone: normalizeOptional(values.timezone),
    };
    onSave(body);
  };

  return (
    <Form<ScreenConfigValues>
      form={form}
      layout="vertical"
      // Remonta con la pantalla para precargar los valores actuales del detalle.
      key={`${screen.id}:${screen.updatedAt}`}
      initialValues={{
        name: screen.name,
        locationId: screen.locationId ?? undefined,
        allyId: screen.allyId ?? undefined,
        resolutionWidth: screen.resolutionWidth,
        resolutionHeight: screen.resolutionHeight,
        orientation: screen.orientation,
        timezone: screen.timezone ?? undefined,
      }}
      onFinish={submit}
    >
      <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Indica el nombre de la pantalla.' }]}>
        <Input placeholder="Sala 1" maxLength={120} />
      </Form.Item>

      <Space size="middle" style={{ display: 'flex', flexWrap: 'wrap' }}>
        <Form.Item name="locationId" label="Ubicación (ID)" style={{ minWidth: 260 }}>
          <Input placeholder="UUID de la ubicación" allowClear />
        </Form.Item>
        <Form.Item name="allyId" label="Aliado (ID)" style={{ minWidth: 260 }}>
          <Input placeholder="UUID del aliado" allowClear />
        </Form.Item>
      </Space>

      <Space size="middle" style={{ display: 'flex', flexWrap: 'wrap' }}>
        <Form.Item name="resolutionWidth" label="Resolución (ancho)">
          <InputNumber min={1} style={{ width: 160 }} placeholder="1920" />
        </Form.Item>
        <Form.Item name="resolutionHeight" label="Resolución (alto)">
          <InputNumber min={1} style={{ width: 160 }} placeholder="1080" />
        </Form.Item>
        <Form.Item name="orientation" label="Orientación" style={{ minWidth: 180 }}>
          <Select allowClear placeholder="Sin definir" options={ORIENTATION_OPTIONS} aria-label="Orientación" />
        </Form.Item>
      </Space>

      <Form.Item name="timezone" label="Zona horaria">
        <Input placeholder="America/Bogota" allowClear style={{ maxWidth: 320 }} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          Guardar configuración
        </Button>
      </Form.Item>
    </Form>
  );
}

/** Normaliza un texto opcional: vacío o solo espacios → `null` (contrato). */
function normalizeOptional(value: string | undefined | null): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}
